import {
  Box,
  Flex,
  Text,
  Button,
  Avatar,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Icon,
} from '@chakra-ui/react';
import { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useShowToast } from '@/hooks';
import {
  useGetInterviewScorecardsQuery,
  useSubmitScoreCardMutation,
} from '@/store/query/ats-applications';
import { ScorecardBody } from '@/components/ats/collect-score-card/ScorecardBody';
import { ContactDetails } from '@/components/contacts/details';
import { PDFReaderBrowser } from '@/components/file-viewers/reactPdf';
import { CenterSpinner } from '@/components/ats/loadders';
import { useScorecardAutoSaveHelper } from '@/components/ats/collect-score-card/useScorecardAutoSaveHelper';
import { AutoSaveLoadingIndicator } from '@/components/ats/collect-score-card/AutoSaveLoadingIndicator';
import { EmptyResults } from '@/components/ats/empty-results';
import NoTask from '@/assets/icons/ats/NoTask';
import { http } from '@/lib/ats-https';
import { X } from '@kula-ai/icons-core';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  application: { id: string; contact?: any };
  reviewId: string;
  onSuccess?: () => void;
  showOnlyCurrUser?: boolean;
  canFillForOthers?: boolean;
  expandOnly?: string | null;
};

export const ReviewScoreCardMobile = ({
  isOpen,
  onClose,
  application,
  reviewId,
  onSuccess,
  showOnlyCurrUser = true,
  canFillForOthers = false,
  expandOnly = null,
}: Props) => {
  const formRef = useRef<any>(null);
  const toast = useShowToast();
  const currUser = useSelector(selectCurrUser);
  const [activeTab, setActiveTab] = useState(0);

  const { data, isLoading } = useGetInterviewScorecardsQuery(
    {
      applicationId: application?.id,
      params: reviewId ? { ats_review_id: reviewId } : {},
    },
    { skip: !application?.id }
  );

  const [resumeData, setResumeData] = useState<any>(null);
  const [resumeLoading, setResumeLoading] = useState<boolean>(true); // Start as true to wait for initial fetch
  const hasResume = !resumeLoading && resumeData;

  useEffect(() => {
    let mounted = true;
    const fetchResume = async () => {
      if (!application?.id) return;
      setResumeLoading(true);
      try {
        const response = await http.get(
          `/ats_applications/${application?.id}/resume`
        );
        if (mounted) setResumeData(response?.data);
      } catch (error) {
        console.error('Error fetching resume:', error);
      } finally {
        if (mounted) setResumeLoading(false);
      }
    };
    fetchResume();
    return () => {
      mounted = false;
    };
  }, [application?.id]);

  // Clamp activeTab when tab availability changes (to avoid pointing at a non-existent panel)
  const maxTabIndex = hasResume ? 2 : 1;
  useEffect(() => {
    if (activeTab > maxTabIndex) setActiveTab(0);
  }, [activeTab, maxTabIndex]);

  const firstScorecard = data?.[0];

  const currUserScorecard = data?.find(
    (sc) => sc?.reviewer?.id === currUser?.id
  );

  /** Empty state only on Scorecard tab — after load, no row for current user (or no scorecards when showing all). */
  const showScorecardUnavailable =
    !isLoading &&
    (showOnlyCurrUser
      ? !currUserScorecard
      : !data?.length || !firstScorecard?.id);

  const candidateForDisplay = firstScorecard?.candidate ?? application?.contact;

  const scorecardUnavailableEmptyProps = {
    icon: NoTask,
    wrapperProps: {
      minH: '240px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    subText: 'SCORECARD UNAVAILABLE',
    subTextProps: { color: 'secondary-600' },
    text: 'This scorecard is no longer accepting responses',
    textProps: { color: 'secondary-900' },
    iconProps: { h: '140px', w: '130px', mb: '0px' },
  } as const;

  const scorecardTabIndex = hasResume ? 2 : 1;

  const canSubmit =
    canFillForOthers || data?.some((sc) => sc?.reviewer?.id === currUser?.id);

  const { handleAutoSave, isSaved } = useScorecardAutoSaveHelper({
    atsApplicationId: application?.id,
    scorecardId: showOnlyCurrUser ? currUserScorecard?.id : firstScorecard?.id,
    questions: showOnlyCurrUser
      ? currUserScorecard?.questions
      : firstScorecard?.questions,
  });

  const [submitScoreCard, { isLoading: isSubmitting }] =
    useSubmitScoreCardMutation();

  /** SAME submit logic as web */
  const handleSubmit = async (values: any) => {
    const scorecard = showOnlyCurrUser ? currUserScorecard : firstScorecard;

    if (!scorecard) return;

    const payload = {
      rating: values?.rating,
      feedback: values?.feedback,
      questions:
        scorecard?.questions?.map((q, idx) => ({
          question: q?.question,
          answer: values?.[idx + 1],
        })) || [],
      status: 'submitted',
    };

    try {
      await submitScoreCard({
        applicationId: scorecard?.ats_application_id,
        scorecardId: scorecard?.id,
        payload,
      }).unwrap();

      toast({ title: 'Scorecard submitted successfully' });
      onSuccess?.();
      onClose();
    } catch {
      toast({
        title: 'Scorecard submission failed',
        status: 'error',
      });
    }
  };

  /** SAME submit trigger as web */
  const handleFormSubmit = () => {
    if (!formRef.current) return;

    formRef.current.submitForm();

    const errArray = Object.keys(formRef.current?.errors || {});
    if (errArray?.length > 0) {
      const el = document.querySelector(`label[for="${errArray[0]}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  if (!isOpen) return null;

  return (
    <Box
      position="fixed"
      inset="0"
      bg="white"
      zIndex="1400"
      display="flex"
      flexDirection="column"
    >
      {/* HEADER */}
      <Flex
        px="16px"
        py="12px"
        align="center"
        borderBottom="1px solid"
        borderColor="border-color"
        gap="12px"
      >
        <Icon as={X} w="20px" h="20px" cursor="pointer" onClick={onClose} />
        <Flex align="center" gap="10px">
          <Avatar
            size="sm"
            name={candidateForDisplay?.name}
            src={candidateForDisplay?.avatar}
          />
          <Box>
            <Text fontWeight="600">{candidateForDisplay?.name}</Text>
            <Text fontSize="sm" color="secondary-600">
              Review Scorecard
            </Text>
          </Box>
        </Flex>
      </Flex>

      {/* BODY — tabs always; scorecard empty state only on Scorecard tab */}
      <Box flex="1" minH={0}>
        <Tabs
          isFitted
          h="100%"
          display="flex"
          flexDirection="column"
          index={activeTab}
          onChange={setActiveTab}
        >
          {/* TAB HEADER */}
          <TabList
            sx={{ borderBottom: '1px solid', borderColor: 'border-color' }}
          >
            <Tab
              border="none"
              _selected={{
                color: 'primary-500',
                borderBottom: '2px solid',
                borderColor: 'primary-500',
              }}
              _focus={{ boxShadow: 'none' }}
            >
              Candidate Info
            </Tab>

            {hasResume && (
              <Tab
                border="none"
                _selected={{
                  color: 'primary-500',
                  borderBottom: '2px solid',
                  borderColor: 'primary-500',
                }}
                _focus={{ boxShadow: 'none' }}
              >
                Resume
              </Tab>
            )}

            <Tab
              border="none"
              _selected={{
                color: 'primary-500',
                borderBottom: '2px solid',
                borderColor: 'primary-500',
              }}
              _focus={{ boxShadow: 'none' }}
            >
              Scorecard
            </Tab>
          </TabList>

          {/* TAB CONTENT */}
          <TabPanels flex="1" minH={0}>
            <TabPanel h="100%" p="12px" overflowY="auto">
              <ContactDetails contact={candidateForDisplay} isMobile={true} />
            </TabPanel>

            {hasResume && (
              <TabPanel h="100%" p="0" minH={0} display="flex">
                <Box flex="1" minH={0}>
                  <PDFReaderBrowser
                    file={resumeData?.url}
                    content_type={resumeData?.content_type}
                    preview_url={resumeData?.preview?.url}
                    fileName={candidateForDisplay?.name}
                    acceptedFiles={[
                      'application/pdf',
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    ]}
                    maxFiles={1}
                  />
                </Box>
              </TabPanel>
            )}

            <TabPanel h="100%" p="16px 4px" overflowY="auto">
              {isLoading ? (
                <CenterSpinner minH="280px" />
              ) : showScorecardUnavailable ? (
                <EmptyResults {...scorecardUnavailableEmptyProps} />
              ) : (
                <ScorecardBody
                  scoreCardDetails={
                    showOnlyCurrUser
                      ? currUserScorecard
                        ? [currUserScorecard]
                        : []
                      : data || []
                  }
                  transcript={null}
                  formRef={formRef}
                  handleSubmit={handleSubmit as unknown as () => void}
                  showOnlyCurrUser={showOnlyCurrUser}
                  canFillForOthers={canFillForOthers}
                  canSubmit={canSubmit}
                  skipHeader
                  handleAutoSave={handleAutoSave}
                  expandOnly={expandOnly}
                />
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* FOOTER — Scorecard tab only, and only when there is a scorecard to submit */}
      {activeTab === scorecardTabIndex &&
        !showScorecardUnavailable &&
        (showOnlyCurrUser ? !!currUserScorecard : !!firstScorecard?.id) && (
          <Box borderTop="1px solid" borderColor="border-color" p="12px">
            <Flex justify="space-between" mb="8px">
              <AutoSaveLoadingIndicator isSaved={isSaved} />
            </Flex>

            <Button
              w="100%"
              height="50px"
              minH="50px"
              variant="primary"
              onClick={handleFormSubmit}
              isLoading={isSubmitting}
            >
              Submit
            </Button>
          </Box>
        )}
    </Box>
  );
};
